import React from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { type UseCampaignContent, useCampaignContent } from '../../hooks/useCampaignContent';
import { sortByOrder } from '../../model/CMS';
import type { SearchFilters } from '../../model/SearchFilters';
import RouteEnum from '../../RouteEnum';
import type { ReduxState } from '../../store';
import { isSignedInSelector } from '../../store/selectors';
import { SkodaXLogo } from '../Icons/SkodaXLogo';
import { StaticImage } from '../Image';
import Link from '../Link';
import SearchBar from '../Search/SearchBar';
import T, { TContext, useTranslation } from '../Translate';

import styles from './homepage.module.scss';

interface HeroProps {
  onSearch(filters: Partial<SearchFilters>): unknown;
}

export default function HomepageHero(props: HeroProps) {
  const campaignContent = useCampaignContent();
  const isSignedIn = useSelector(isSignedInSelector);

  if (campaignContent) {
    return <HomepageHeroCampaign {...campaignContent} />;
  }

  if (!isSignedIn) {
    return (
      <TContext name="guest">
        <HomepageHeroGuest {...props} />
      </TContext>
    );
  }
  return <HomepageHeroUser {...props} />;
}

function HomepageHeroGuest(props: HeroProps) {
  const { t } = useTranslation();
  const titleEmVariants = t<string[] | undefined>('homepage.hero.titleEmphasis');

  return (
    <>
      <div className={styles.heroGuest}>
        <h1>
          <T as="span" id="homepage.hero.title" />
          <span className={styles.emphasis}>
            <span className={styles.emphasisInner}>
              {titleEmVariants && Array.isArray(titleEmVariants) && titleEmVariants.length
                ? titleEmVariants.map((it, i) => <em key={i}>{it}</em>)
                : null}
            </span>
          </span>
        </h1>
      </div>

      <div className={styles.searchbarSection}>
        <SearchBar className={styles.searchbar} onSubmit={props.onSearch} />
      </div>

      <div className={styles.heroGuest}>
        <Trust />
      </div>

      <div className={styles.heroImage}>
        <StaticImage alt="" height={960} name="hp_hero" width={1440} />
      </div>
    </>
  );
}

function HomepageHeroUser(props: HeroProps) {
  const { username, hasBasicInfo } = useSelector((store: ReduxState) => ({
    hasBasicInfo: (store.user?.data?.forename ?? '').length > 0,
    username: store.user?.short_name,
  }));

  return (
    <div className={styles.hero}>
      <T as="h1" context={!hasBasicInfo ? 'noName' : null} data={{ username }} html id="homepage.hero.title_tmp" />
      <SearchBar className={styles.searchbar} onSubmit={props.onSearch} />
      <Trust />
    </div>
  );
}

function HomepageHeroCampaign({ header, subheader, heroButtons, button, filtersQuery, image }: UseCampaignContent) {
  const buttons = heroButtons.length
    ? heroButtons.sort(sortByOrder).map((it, index) => (
        <a
          className={cn('button', it.className, typeof it.icon === 'object' && 'button-icon')}
          href={it.url}
          key={index}
        >
          {it.text}
          {it.icon ? (
            <i
              className={cn('icon', `icon-${it.icon.type}`, {
                'icon-right': it.icon.side === 'right',
              })}
            />
          ) : null}
        </a>
      ))
    : [
        <Link className="button button-secondary button-icon" key={0} search={filtersQuery} to={RouteEnum.RENT_CAR}>
          {button}
          <i className="icon icon-search" />
        </Link>,
        <Link className="button button-mono button-outline" key={1} to={RouteEnum.RENT_CAR}>
          <T id="homepage.hero.searchAll" />
        </Link>,
      ];

  return (
    <div className={styles.heroCampaign}>
      <div className={styles.content}>
        <h1 className="display" dangerouslySetInnerHTML={{ __html: header }} />
        <p dangerouslySetInnerHTML={{ __html: subheader }} />

        <div className={styles.buttons}>{buttons}</div>

        <Trust />
      </div>
      <div className={styles.imageWrapper}>
        <StaticImage className={styles.image} name={`hp_${image}`} />
      </div>
    </div>
  );
}

function Trust() {
  return (
    <div className={styles.trust}>
      <T id="homepage.hero.trust.1" />
      <img alt="Uniqa" height={20} src="/img/hp_trust_uniqa.svg" width={95} />
      <T id="homepage.hero.trust.2" />
      <SkodaXLogo className={styles.image} />
    </div>
  );
}
